<template>
  <div class="container-fluid p-0 h-100">
    <h3><strong>Cutoffs</strong> windows</h3>

    <!-- ── Region Cutoffs Bulk Upload ── -->
    <div class="mb-4">
      <div class="d-flex align-items-center mb-2">
        <h5 class="mb-0 mr-3">Region Cutoffs (Holiday)</h5>
        <b-button size="sm" variant="outline-secondary" class="mr-2" @click="downloadRegionCutoffTemplate">
          <download-icon size="1x" class="mr-1"></download-icon>
          Download CSV Template
        </b-button>
        <b-button
          size="sm"
          variant="info"
          :disabled="!hasValidRegionCutoffFile"
          @click="submitRegionCutoffs"
        >
          <upload-icon size="1x" class="mr-1"></upload-icon>
          Upload
        </b-button>
      </div>

      <b-form-group class="mb-1" label-for="region-cutoff-file">
        <b-form-file
          id="region-cutoff-file"
          v-model="regionCutoffFile"
          accept=".csv"
          placeholder="Choose a .csv file..."
          @change="onRegionCutoffFileChange"
        ></b-form-file>
        <small class="text-muted mt-1 d-block">
          Required columns: region_name, available_date (YYYY-MM-DD), cutoff_time (HH:mm), cutoff_effect (0: Same-Day, 1: Next-Day)
        </small>
      </b-form-group>

      <!-- Validation errors -->
      <b-alert v-if="regionCutoffErrors.length > 0" variant="danger" show class="mt-2">
        <strong>Validation errors — please fix your CSV and re-upload:</strong>
        <ul class="mb-0 mt-1">
          <li v-for="(err, i) in regionCutoffErrors" :key="i">{{ err }}</li>
        </ul>
      </b-alert>

      <!-- Preview table -->
      <div v-if="regionCutoffPreview.length > 0" class="mt-2">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <strong>Preview ({{ regionCutoffPreview.length }} row{{ regionCutoffPreview.length !== 1 ? 's' : '' }})</strong>
          <b-badge variant="success">Ready to upload</b-badge>
        </div>
        <b-table
          :items="regionCutoffPreview.slice(0, 10)"
          :fields="regionCutoffPreviewFields"
          small
          bordered
          striped
          responsive
          class="mt-1"
        >
          <template #cell(valid)="data">
            <b-badge :variant="data.item._valid ? 'success' : 'danger'">
              {{ data.item._valid ? 'OK' : 'Error' }}
            </b-badge>
          </template>
          <template #cell(cutoff_effect_label)="data">
            <b-badge
              :variant="data.item.cutoff_effect === '0' ? 'warning' : data.item.cutoff_effect === '1' ? 'info' : 'secondary'"
            >
              {{
                data.item.cutoff_effect === '0' ? 'Same-Day' :
                data.item.cutoff_effect === '1' ? 'Next-Day' :
                data.item.cutoff_effect === '2' ? 'Next-Day +1' :
                data.item.cutoff_effect === '3' ? 'Next-Day +2' :
                data.item.cutoff_effect === '4' ? 'Next-Day +3' :
                data.item.cutoff_effect
              }}
            </b-badge>
          </template>
        </b-table>
        <p v-if="regionCutoffPreview.length > 10" class="text-muted small">
          Showing first 10 of {{ regionCutoffPreview.length }} rows.
        </p>
      </div>

      <!-- Upload progress overlay -->
      <b-overlay :show="regionCutoffUploading" rounded="sm" class="mt-2">
        <template #overlay>
          <div class="text-center">
            <b-spinner type="grow" variant="info" label="Uploading..."></b-spinner>
            <p class="mt-2">
              Uploading {{ regionCutoffProgress.current }} of {{ regionCutoffProgress.total }}...
            </p>
          </div>
        </template>
        <div v-if="regionCutoffResults.length > 0" class="mt-3">
          <strong>Upload Results:</strong>
          <b-table
            :items="regionCutoffResults"
            :fields="regionCutoffResultFields"
            small
            bordered
            striped
            responsive
            class="mt-1"
          >
            <template #cell(result)="data">
              <b-badge :variant="data.item.success ? 'success' : 'danger'">
                {{ data.item.success ? 'Success' : 'Failed' }}
              </b-badge>
            </template>
          </b-table>
        </div>
      </b-overlay>

      <hr />
    </div>

    <!-- ── Existing Cutoff Windows Grid ── -->
    <cutoff-time-grid ref="cutoffGrid"></cutoff-time-grid>
  </div>
</template>

<script>
import { DownloadIcon, UploadIcon } from "vue-feather-icons";
import ToastMixin from "@/mixin/toast-mixin";
import CutoffTimeGrid from "@/components/grids/cutoff-time-grid.vue";

const REGION_CUTOFF_COLUMNS = [
  "region_name",
  "available_date",
  "cutoff_time",
  "cutoff_effect"
];

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_REGEX = /^\d{1,2}:\d{2}(:\d{2})?$/;
const EFFECT_REGEX = /^\d{1}$/;

export default {
  mixins: [ToastMixin],
  components: {
    CutoffTimeGrid,
    DownloadIcon,
    UploadIcon
  },
  data() {
    return {
      // Region cutoff bulk upload
      regionCutoffFile: null,
      regionCutoffPreview: [],
      regionCutoffErrors: [],
      regionCutoffUploading: false,
      regionCutoffProgress: { current: 0, total: 0 },
      regionCutoffResults: [],
      regionCutoffPreviewFields: [
        { key: "region_name", label: "Region Name" },
        { key: "available_date", label: "Available Date" },
        { key: "cutoff_time", label: "Cutoff Time" },
        { key: "cutoff_effect_label", label: "Cutoff Effect" },
        { key: "valid", label: "Valid" }
      ],
      regionCutoffResultFields: [
        { key: "row", label: "Row" },
        { key: "name", label: "Region" },
        { key: "result", label: "Result" },
        { key: "message", label: "Message" }
      ]
    };
  },
  computed: {
    hasValidRegionCutoffFile() {
      return (
        this.regionCutoffPreview.length > 0 &&
        this.regionCutoffErrors.length === 0
      );
    }
  },
  methods: {
    // ── Template download ───────────────────────────────────────────────────

    downloadRegionCutoffTemplate() {
      const headers = REGION_CUTOFF_COLUMNS.join(",");
      const example = "Winnipeg,2024-12-26,14:00,1";
      this._triggerCsvDownload(
        `${headers}\n${example}`,
        "region_cutoff_holiday_template.csv"
      );
    },

    _triggerCsvDownload(csvContent, filename) {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    // ── File change ─────────────────────────────────────────────────────────

    onRegionCutoffFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.regionCutoffErrors = [];
      this.regionCutoffPreview = [];
      this.regionCutoffResults = [];
      this._readCsvFile(file, (rows) => {
        const { errors, data } = this._validateRegionCutoffRows(rows);
        this.regionCutoffErrors = errors;
        this.regionCutoffPreview = data;
      });
    },

    // ── CSV parsing ─────────────────────────────────────────────────────────

    _readCsvFile(file, callback) {
      const reader = new FileReader();
      reader.onload = (e) => {
        callback(this._parseCsv(e.target.result));
      };
      reader.readAsText(file);
    },

    _parseCsv(text) {
      const lines = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .filter((l) => l.trim() !== "");

      if (lines.length < 2) return [];

      const headers = this._splitCsvLine(lines[0]).map((h) =>
        h.trim().toLowerCase()
      );
      return lines.slice(1).map((line, idx) => {
        const values = this._splitCsvLine(line);
        const row = {};
        headers.forEach((h, i) => {
          row[h] = (values[i] || "").trim();
        });
        row._rowNumber = idx + 2;
        return row;
      });
    },

    _splitCsvLine(line) {
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += ch;
        }
      }
      result.push(current);
      return result;
    },

    // ── Validation ──────────────────────────────────────────────────────────

    _validateRegionCutoffRows(rows) {
      const errors = [];
      const data = [];

      if (rows.length === 0) {
        errors.push("The file is empty or contains only a header row.");
        return { errors, data };
      }

      const missingCols = REGION_CUTOFF_COLUMNS.filter(
        (col) => !(col in rows[0])
      );
      if (missingCols.length > 0) {
        errors.push(
          `Missing required column(s): ${missingCols.join(", ")}. ` +
            `Expected: ${REGION_CUTOFF_COLUMNS.join(", ")}`
        );
        return { errors, data };
      }

      rows.forEach((row) => {
        const rowErrors = [];

        if (!row.region_name) rowErrors.push("region_name is required");

        if (!row.available_date) {
          rowErrors.push("available_date is required");
        } else if (!DATE_REGEX.test(row.available_date)) {
          rowErrors.push(`available_date "${row.available_date}" must be YYYY-MM-DD`);
        }

        if (!row.cutoff_time) {
          rowErrors.push("cutoff_time is required");
        } else if (!TIME_REGEX.test(row.cutoff_time)) {
          rowErrors.push(
            `cutoff_time "${row.cutoff_time}" must be H:mm or H:mm:ss`
          );
        }

        if (!row.cutoff_effect) {
          rowErrors.push("cutoff_effect is required");
        } else if (!EFFECT_REGEX.test(row.cutoff_effect)) {
          rowErrors.push(
            `cutoff_effect "${row.cutoff_effect}" must be 0-9`
          );
        }

        if (rowErrors.length > 0) {
          rowErrors.forEach((e) =>
            errors.push(`Row ${row._rowNumber}: ${e}`)
          );
          data.push({ ...row, _valid: false });
        } else {
          data.push({ ...row, _valid: true });
        }
      });

      return { errors, data };
    },

    // ── Submit ──────────────────────────────────────────────────────────────

    async submitRegionCutoffs() {
      const rows = this.regionCutoffPreview.filter((r) => r._valid);
      if (rows.length === 0) return;

      this.regionCutoffResults = [];
      this.regionCutoffUploading = true;
      this.regionCutoffProgress = { current: 0, total: rows.length };

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        this.regionCutoffProgress.current = i + 1;

        const regionId = await this._resolveRegionId(row.region_name);
        if (!regionId) {
          this.regionCutoffResults.push({
            row: row._rowNumber,
            name: row.region_name,
            success: false,
            message: `Region "${row.region_name}" not found. Check that region_name matches exactly.`
          });
          failCount++;
          continue;
        }

        const cutoffTime =
          row.cutoff_time.length === 5
            ? `${row.cutoff_time}:00`
            : row.cutoff_time;

        const payload = {
          type: "datesAvailable",
          availableDates: row.available_date,
          cutoffTime: cutoffTime.substring(0, 5),
          cutoffEffect: row.cutoff_effect
        };

        try {
          const response = await this.axios({
            method: "PUT",
            url: `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/region-rules/${regionId}/rule`,
            headers: {
              Authorization: `Bearer ${this.$store.getters.idToken}`
            },
            data: payload
          });

          if (response.status === 201 || response.status === 200) {
            this.regionCutoffResults.push({
              row: row._rowNumber,
              name: row.region_name,
              success: true,
              message: response.data["success"] || "Created"
            });
            successCount++;
          } else {
            this.regionCutoffResults.push({
              row: row._rowNumber,
              name: row.region_name,
              success: false,
              message:
                response.data["error"] ||
                `Unexpected status ${response.status}`
            });
            failCount++;
          }
        } catch (err) {
          this.regionCutoffResults.push({
            row: row._rowNumber,
            name: row.region_name,
            success: false,
            message:
              err?.response?.data?.error || err.message || "Request failed"
          });
          failCount++;
        }
      }

      this.regionCutoffUploading = false;

      if (successCount > 0) {
        this.$refs.cutoffGrid.loadData();
      }

      this.displayMessage(
        successCount > 0 ? "success" : "warning",
        successCount > 0
          ? `Bulk upload complete: ${successCount} record(s) created` +
            (failCount > 0 ? `, ${failCount} failed.` : ".")
          : `Bulk upload failed: all ${failCount} record(s) encountered errors.`
      );
    },

    async _resolveRegionId(regionName) {
      let regions = this.$store.getters.regions;
      if (!regions || regions.length === 0) {
        const response = await this.axios({
          method: "GET",
          url: `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/places/region`,
          headers: {
            Authorization: `Bearer ${this.$store.getters.idToken}`
          }
        });
        this.$store.commit("setRegions", response.data);
        regions = this.$store.getters.regions;
      }
      console.log("regions:", regions);
      const match = regions.find(
        r => (r.tag || "").toLowerCase() === regionName.toLowerCase()
      );
      return match ? match.region_id : null;
    }
  }
};
</script>

<style></style>
